import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useBankID } from '../../hooks/useBankID';
import '../../scss/bank-id-auth.scss';
import { bankidcolor, CloseIcon, externalLinkBlack, Softwareqr } from '../images/svg/icons';
import BankIDQRModal from '../bank-id-qr-code/bank-id-qr-modal';
import ReactivateModal from '../reactivatemodal/reactivate.modal';
import { getItem, setItem } from '../../helper/localstorage.helper';

export interface BankIDComponentProps {
    mode: 'login' | 'signup';
    accountType?: 'freelancer' | 'business';
    onSuccess?: (userData: any) => void;
    onError?: (error: string) => void;
    onClose?: () => void;
    title?: string;
    className?: string;
    showCloseButton?: boolean;
    autoInitialize?: boolean;
}

/**
 * Unified BankID Component that follows Redux patterns
 * This component can be used for both login and signup flows
 */
const BankIDComponent: React.FC<BankIDComponentProps> = ({
    mode,
    accountType,
    onSuccess,
    onError,
    onClose,
    title,
    className = '',
    showCloseButton = true,
    autoInitialize = true,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();


    const {
        isInitializing,
        isQRModalOpen,
        qrState,
        qrCodeData,
        isAuthSuccessful,
        needsReload,
        canUseSameDevice,
        canUseQRCode,
        toggleReactivate,
        inactiveAccount,
        userData,
        error,
        initializeQRFlow,
        authenticateSameDevice,
        handleQRReload,
        closeQRModal,
        getTitle,
        handleReactivationClose,
        handleReactivationSuccess,
    } = useBankID({
        mode,
        accountType,
        onSuccess,
        onError,
        autoInitialize,
    });

    const handleBankIDAuth = async (method: 'same-device' | 'qr-code') => {
        if (method === 'qr-code') {
            await initializeQRFlow();
        } else if (method === 'same-device') {
            await authenticateSameDevice();
        }
    };

    const handleCloseQRModal = () => {
        closeQRModal();
    };

    const handleBackFromQR = () => {
        closeQRModal();
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };
    const userEmail = userData?.email || getItem('userEmail') || '';

    return (
        <div className={`bankid-auth-overlay ${className}`}>
            <div className="bankid-auth-modal">
                <div className="bankid-content border-b-2">
                    <div className='d-flex justify-content-between align-items-start'>
                        <h2 className="bankid-title mb-2">{title || getTitle()}</h2>
                        {showCloseButton && onClose && (
                            <button
                                onClick={handleClose}
                                className="close-btn d-flex pt-0"
                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            >
                                <img src={CloseIcon} alt="Close" height={16} width={16} />
                            </button>
                        )}
                    </div>

                    <div style={{ borderTop: '1px solid #E9EAEB' }} />

                    <div className="bankid-options border-t-2 mt-4">
                        <button
                            className="bankid-option"
                            onClick={() => handleBankIDAuth('same-device')}
                            disabled={isInitializing || !canUseSameDevice || toggleReactivate}
                        >
                            <div className="option-icon">
                                <img src={bankidcolor} alt="" />
                            </div>
                            <div className="option-content">
                                <h3>{t('OPEN_BANK_ID_ON_SAME_DEVICE')}</h3>
                                <div className="option-link">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width={14}
                                        height={14}
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <path
                                            d="M12.5 4.33333C12.5 4.60948 12.7239 4.83333 13 4.83333C13.2761 4.83333 13.5 4.60948 13.5 4.33333H12.5ZM9.66667 0.5C9.39052 0.5 9.16667 0.723858 9.16667 1C9.16667 1.27614 9.39052 1.5 9.66667 1.5V0.5ZM6.64645 6.64645C6.45118 6.84171 6.45118 7.15829 6.64645 7.35355C6.84171 7.54882 7.15829 7.54882 7.35355 7.35355L6.64645 6.64645ZM12.9637 1.182L12.5182 1.40899L12.5182 1.409L12.9637 1.182ZM12.818 1.03633L12.591 1.48183V1.48184L12.818 1.03633ZM7 2.83333C7.27614 2.83333 7.5 2.60948 7.5 2.33333C7.5 2.05719 7.27614 1.83333 7 1.83333V2.83333ZM12.1667 7C12.1667 6.72386 11.9428 6.5 11.6667 6.5C11.3905 6.5 11.1667 6.72386 11.1667 7H12.1667ZM2.45603 12.7094L2.68302 12.2638L2.45603 12.7094ZM1.29065 11.544L0.845146 11.771L1.29065 11.544ZM10.2106 12.7094L9.98365 12.2638L10.2106 12.7094ZM11.376 11.544L10.9305 11.317L11.376 11.544ZM1.29065 3.78936L0.845146 3.56236H0.845146L1.29065 3.78936ZM2.45603 2.62398L2.22903 2.17848L2.22903 2.17848L2.45603 2.62398ZM13 4.33333H13.5V1.53333H13H12.5V4.33333H13ZM12.4667 1V0.5H9.66667V1V1.5H12.4667V1ZM12.9024 1.09763L12.5488 0.744078L6.64645 6.64645L7 7L7.35355 7.35355L13.2559 1.45118L12.9024 1.09763ZM13 1.53333H13.5C13.5 1.44824 13.5004 1.35435 13.4938 1.27368C13.4867 1.18718 13.4692 1.07289 13.4092 0.955008L12.9637 1.182L12.5182 1.409C12.4944 1.36242 12.4951 1.33045 12.4971 1.35512C12.4981 1.36715 12.499 1.38617 12.4995 1.41757C12.5 1.44895 12.5 1.48552 12.5 1.53333H13ZM12.4667 1V1.5C12.5145 1.5 12.5511 1.50001 12.5824 1.50051C12.6138 1.501 12.6329 1.5019 12.6449 1.50288C12.6695 1.5049 12.6376 1.50557 12.591 1.48183L12.818 1.03633L13.045 0.590827C12.9271 0.530762 12.8128 0.51327 12.7263 0.506202C12.6456 0.499611 12.5518 0.5 12.4667 0.5V1ZM12.9637 1.182L13.4092 0.955012C13.3692 0.876609 13.3174 0.80553 13.2559 0.744078L12.9024 1.09763L12.5488 1.45118C12.5365 1.4389 12.5262 1.42468 12.5182 1.40899L12.9637 1.182ZM12.9024 1.09763L13.2559 0.744078C13.1945 0.682625 13.1234 0.630774 13.045 0.590827L12.818 1.03633L12.591 1.48184C12.5753 1.47385 12.5611 1.46347 12.5488 1.45118L12.9024 1.09763ZM7.4 13V12.5H5.26667V13V13.5H7.4V13ZM1 8.73333H1.5V6.6H1H0.5V8.73333H1ZM5.26667 2.33333V2.83333H7V2.33333V1.83333H5.26667V2.33333ZM11.6667 7H11.1667V8.73333H11.6667H12.1667V7H11.6667ZM5.26667 13V12.5C4.51168 12.5 3.97551 12.4996 3.55592 12.4653C3.14217 12.4315 2.886 12.3673 2.68302 12.2638L2.45603 12.7094L2.22903 13.1549C2.59648 13.3421 2.9989 13.4232 3.47449 13.462C3.94425 13.5004 4.52818 13.5 5.26667 13.5V13ZM1 8.73333H0.5C0.5 9.47182 0.499611 10.0558 0.537992 10.5255C0.576849 11.0011 0.657921 11.4035 0.845146 11.771L1.29065 11.544L1.73615 11.317C1.63273 11.114 1.56848 10.8578 1.53467 10.4441C1.50039 10.0245 1.5 9.48832 1.5 8.73333H1ZM2.45603 12.7094L2.68302 12.2638C2.27534 12.0561 1.94388 11.7247 1.73615 11.317L1.29065 11.544L0.845146 11.771C1.14875 12.3668 1.63318 12.8513 2.22903 13.1549L2.45603 12.7094ZM7.4 13V13.5C8.13849 13.5 8.72242 13.5004 9.19218 13.462C9.66777 13.4232 10.0702 13.3421 10.4376 13.1549L10.2106 12.7094L9.98365 12.2638C9.78066 12.3673 9.5245 12.4315 9.11075 12.4653C8.69116 12.4996 8.15499 12.5 7.4 12.5V13ZM11.6667 8.73333H11.1667C11.1667 9.48832 11.1663 10.0245 11.132 10.4441C11.0982 10.8578 11.0339 11.114 10.9305 11.317L11.376 11.544L11.8215 11.771C12.0087 11.4035 12.0898 11.0011 12.1287 10.5255C12.1671 10.0558 12.1667 9.47182 12.1667 8.73333H11.6667ZM10.2106 12.7094L10.4376 13.1549C11.0335 12.8513 11.5179 12.3668 11.8215 11.771L11.376 11.544L10.9305 11.317C10.7228 11.7247 10.3913 12.0561 9.98365 12.2638L10.2106 12.7094ZM1 6.6H1.5C1.5 5.84501 1.50039 5.30884 1.53467 4.88925C1.56848 4.4755 1.63273 4.21934 1.73615 4.01635L1.29065 3.78936L0.845146 3.56236C0.657921 3.92981 0.576849 4.33223 0.537992 4.80782C0.499611 5.27758 0.5 5.86151 0.5 6.6H1ZM5.26667 2.33333V1.83333C4.52818 1.83333 3.94425 1.83294 3.47449 1.87133C2.9989 1.91018 2.59648 1.99125 2.22903 2.17848L2.45603 2.62398L2.68302 3.06949C2.886 2.96606 3.14217 2.90181 3.55592 2.868C3.97551 2.83372 4.51168 2.83333 5.26667 2.83333V2.33333ZM1.29065 3.78936L1.73615 4.01635C1.94388 3.60867 2.27534 3.27721 2.68302 3.06949L2.45603 2.62398L2.22903 2.17848C1.63318 2.48208 1.14875 2.96652 0.845146 3.56236L1.29065 3.78936Z"
                                            fill="black"
                                        />
                                    </svg>

                                </div>
                            </div>
                        </button>

                        <button
                            className="bankid-option"
                            onClick={() => handleBankIDAuth('qr-code')}
                            disabled={isInitializing || !canUseQRCode || toggleReactivate}
                        >
                            <div className="option-icon">
                                <div className="qr-icon">
                                    <img src={bankidcolor} alt="" />
                                </div>
                            </div>
                            <div className="option-content">
                                <h3>{t('SCAN_QR_FROM_BANK_APP')}</h3>
                                <img src={Softwareqr} alt="" />
                            </div>
                        </button>
                    </div>

                    {mode === 'signup' && (
                        <div className="bankid-info">
                            <p className="info-text">
                                {t('By signing up with BankID, your personal information will be automatically filled from your bank details.')}
                            </p>
                        </div>
                    )}

                </div>
            </div>

            <BankIDQRModal
                isReloadState={needsReload}
                show={isQRModalOpen}
                onClose={handleCloseQRModal}
                qrCodeValue={qrCodeData?.qrCode || null}
                isLoading={qrState.isLoading}
                onReloadQR={handleQRReload}
                onBack={handleBackFromQR}
                customMessage={qrState.customMessage}
                isAuthenticated={qrState.isAuthenticated}
                isAuthSuccessful={isAuthSuccessful}
            />

            <ReactivateModal
                show={toggleReactivate}
                onClose={handleReactivationClose}
                onSuccess={handleReactivationSuccess}
                email={userEmail}
                qrCodeEmail={userEmail}
                inactiveAccount={inactiveAccount}
                mode={mode}
            />
        </div>
    );
};

export default BankIDComponent;
